import React, { useState } from 'react';
import { usePreventGameKeydownListening } from '@hooks';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

import { IBranding, IBrandingListModalProps } from './interfaces';
import { useStyles } from './styles';

const BrandingListModal = ({
  open,
  onClose,
  onLeaveBrandingMode,
  onCreateNew,
  brandings,
  brandingsLoading = false,
  onToggleBranding,
  onEditBranding,
  onCloneBranding,
  onDeleteBranding,
  currentBrandingId,
  eventName,
}: IBrandingListModalProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = usePreventGameKeydownListening<HTMLInputElement>();
  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement;
    brandingId: string;
    isActive: boolean;
  } | null>(null);

  const getIsCurrent = (branding: IBranding): boolean =>
    currentBrandingId != null
      ? branding.id === currentBrandingId
      : branding.isActive;

  const visibleBrandings = brandings
    .filter((b) => b.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const aIsCurrent = getIsCurrent(a);
      const bIsCurrent = getIsCurrent(b);

      if (aIsCurrent && !bIsCurrent) {
        return -1;
      }
      if (!aIsCurrent && bIsCurrent) {
        return 1;
      }
      return 0;
    });

  const getRelativeTime = (timestamp?: number): string => {
    if (!timestamp) {
      return '';
    }
    const diffDays = Math.floor((Date.now() - timestamp) / 86400000);
    if (diffDays === 0) {
      return translate('adminToolbox.editedToday', 'edited today');
    }
    if (diffDays === 1) {
      return translate('adminToolbox.editedYesterday', 'edited yesterday');
    }
    return translate(
      'adminToolbox.editedDaysAgo',
      'edited {{count}} days ago',
      {
        count: diffDays,
      },
    );
  };

  const renderPalette = (branding: IBranding) => {
    const { brandingColors } = branding;
    const hasPrimary = Boolean(brandingColors?.primaryColor);
    const hasSecondary = Boolean(brandingColors?.secondaryColor);
    const hasOutline = Boolean(brandingColors?.outlineColor);

    if (hasPrimary || hasSecondary || hasOutline) {
      return (
        <>
          <Box
            sx={{
              ...classes.colorStripe,
              backgroundColor:
                brandingColors?.primaryColor || 'rgba(255,255,255,0.1)',
            }}
          />
          <Box
            sx={{
              ...classes.colorStripe,
              backgroundColor:
                brandingColors?.secondaryColor || 'rgba(255,255,255,0.1)',
            }}
          />
          <Box
            sx={{
              ...classes.colorStripe,
              backgroundColor:
                brandingColors?.outlineColor || 'rgba(255,255,255,0.1)',
            }}
          />
        </>
      );
    }
    return <Box sx={classes.palettePlaceholder} />;
  };

  const handleCardClick = (id: string) => {
    onEditBranding?.(id);
  };

  const handleMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    brandingId: string,
    isActive: boolean,
  ) => {
    e.stopPropagation();
    setMenuState({ anchorEl: e.currentTarget, brandingId, isActive });
  };

  const handleMenuClose = () => {
    setMenuState(null);
  };

  const handleClone = () => {
    if (menuState) {
      onCloneBranding?.(menuState.brandingId);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (menuState) {
      onDeleteBranding?.(menuState.brandingId);
    }
    handleMenuClose();
  };

  const handleActivateFromMenu = () => {
    if (menuState) {
      onToggleBranding(menuState.brandingId);
    }
    handleMenuClose();
  };

  const subtitleParts = eventName
    ? [eventName, String(brandings.length)]
    : [String(brandings.length)];
  const subtitle = subtitleParts.join(' · ');

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      sx={classes.modalRoot}
    >
      <Box sx={classes.container}>
        <Box sx={classes.header}>
          <Box sx={classes.headerLeft}>
            <Typography sx={classes.title}>
              {translate(
                'adminToolbox.availableBrandings',
                'Available configurations',
              )}
            </Typography>
            <Typography sx={classes.subtitle}>{subtitle}</Typography>
          </Box>
          <Box sx={classes.headerRight}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onCreateNew}
              sx={classes.createHeaderButton}
            >
              {translate(
                'adminToolbox.createNewBranding',
                'Create new configuration',
              )}
            </Button>
            <IconButton onClick={onClose} sx={classes.closeButton} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={classes.searchWrapper}>
          <TextField
            inputRef={searchInputRef}
            fullWidth
            size="small"
            placeholder={translate(
              'adminToolbox.searchBrandings',
              'Search configurations',
            )}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            onKeyUp={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={classes.searchIcon} />
                </InputAdornment>
              ),
            }}
            sx={classes.searchField}
          />
        </Box>

        <Box sx={classes.content}>
          {brandingsLoading ? (
            <Box sx={classes.stateBox}>
              <CircularProgress size={24} />
              <Typography sx={classes.stateText}>
                {translate(
                  'adminToolbox.loadingBrandings',
                  'Loading configurations...',
                )}
              </Typography>
            </Box>
          ) : visibleBrandings.length === 0 ? (
            <Box sx={classes.stateBox}>
              <Typography sx={classes.stateText}>
                {translate(
                  'adminToolbox.noBrandingsYet',
                  'No configurations yet. Create your first one.',
                )}
              </Typography>
            </Box>
          ) : (
            <Box sx={classes.cardGrid}>
              {visibleBrandings.map((branding) => {
                const isCurrent = getIsCurrent(branding);
                const relTime = getRelativeTime(branding.timestamp);

                return (
                  <Box
                    key={branding.id}
                    sx={[classes.card, isCurrent && classes.cardActive]}
                    onClick={() => handleCardClick(branding.id)}
                  >
                    <Box sx={classes.cardPalette}>
                      {renderPalette(branding)}
                      {isCurrent && (
                        <Box sx={classes.liveBadge}>
                          <Box sx={classes.liveDot} />
                          <Typography sx={classes.liveBadgeText}>
                            {translate('adminToolbox.onAir', 'Active')}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={classes.cardBody}>
                      <Typography sx={classes.cardName}>
                        {branding.name === 'Unnamed'
                          ? translate('adminToolbox.unnamed', 'Unnamed')
                          : branding.name}
                      </Typography>
                      {relTime && (
                        <Typography sx={classes.cardMeta}>{relTime}</Typography>
                      )}

                      <Box sx={classes.cardFooterRow}>
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            handleMenuOpen(e, branding.id, isCurrent)
                          }
                          sx={classes.moreButton}
                          aria-label="more"
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Box sx={classes.footer}>
          <Button
            variant="outlined"
            onClick={onLeaveBrandingMode}
            sx={classes.leaveButton}
          >
            {translate('adminToolbox.leaveEditorMode', 'Leave editor mode')}
          </Button>
        </Box>

        <Menu
          anchorEl={menuState?.anchorEl}
          open={Boolean(menuState)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{ sx: classes.menuPaper }}
        >
          {!menuState?.isActive && (
            <MenuItem onClick={handleActivateFromMenu} sx={classes.menuItem}>
              <PowerSettingsNewIcon sx={classes.menuItemIcon} />
              {translate('adminToolbox.activateBranding', 'Activate configuration')}
            </MenuItem>
          )}
          <MenuItem onClick={handleClone} sx={classes.menuItem}>
            <ContentCopyOutlinedIcon sx={classes.menuItemIcon} />
            {translate('common.copy', 'Copy')}
          </MenuItem>
          <MenuItem
            onClick={handleDelete}
            disabled={menuState?.isActive ?? false}
            sx={classes.menuItem}
          >
            <DeleteOutlineIcon sx={classes.menuItemIcon} />
            {translate('common.delete', 'Delete')}
          </MenuItem>
        </Menu>
      </Box>
    </Modal>
  );
};

export default BrandingListModal;
